import { type EventParams } from '../types/base/omnichat-events'
import { type AnalyticsEvents, type BaseAnalyticsEvent } from '../types/events'
import { DniAnalytics } from './Analytics'
import { type SetUserEvents } from './types'
import { hasOmnichatScript, insertOmnichatEasychatBubble, insertOmnichatScript } from './utils'

export interface OmniChatConfig {
  appKey?: string
  memberId?: string | null
  memberName?: string | null
  memberProfilePic?: string | null
  setUserEvents?: SetUserEvents
}

export class Omnichat extends DniAnalytics<AnalyticsEvents> {
  readonly config: OmniChatConfig

  private static instance: Omnichat | null = null

  constructor(config: OmniChatConfig) {
    super()
    this.config = config
    this.initialize()
  }

  // Static getInstance method for singleton pattern
  public static getInstance(config: OmniChatConfig): Omnichat {
    if (!Omnichat.instance) {
      Omnichat.instance = new Omnichat(config)
    }
    return Omnichat.instance
  }

  private getInitConfig() {
    return {
      memberId: this.config.memberId,
      memberName: this.config.memberName,
      memberProfilePic: this.config.memberProfilePic,
    }
  }

  private configOmnichat() {
    window.omnichatTracker?.push(['init', 'config', this.getInitConfig()])
  }

  private initOmnichat() {
    insertOmnichatEasychatBubble(this.config.appKey)
    if (!hasOmnichatScript()) {
      insertOmnichatScript()
    }
    this.configOmnichat()
  }

  private async afterInitialized() {
    if (this.config.setUserEvents) {
      try {
        await this.config.setUserEvents()
      }
      catch (error) {
        console.error('[OmniChat] afterInitialized setUserEvents', error)
      }
    }
    this.setIsReady(true)
  }

  /**
   * Reference: https://www.notion.so/shepherdtech/OmniChat-Tutorial-for-SD-042ea83f15954564bb1213e51c9a20d4
   * Reference: https://docs.omnichat.ai/integration/tracking-pixel
   */
  protected initialize() {
    if (!this.config.appKey) {
      console.error('[OmniChat] appKey is not defined')
      return
    }
    this.initOmnichat()
    this.afterInitialized()
  }

  protected sendAnalyticsEvent(customEvent: BaseAnalyticsEvent) {
    if (!window.omnichatTracker) {
      console.error('[OmniChat] window.omnichatTracker is not defined')
      return
    }

    try {
      const { eventType = 'event', eventName, eventParams } = customEvent
      if (['set', 'get'].includes(eventType)) {
        console.warn(`[OmniChat] eventType '${eventType}' is not supported, skipping event: ${eventName}`)
        return
      }
      window.omnichatTracker.push([eventType as 'init' | 'event', eventName, eventParams as EventParams])
    }
    catch (error) {
      console.error('[OmniChat] sendEvent', error)
    }
  }
}
